import React from 'react';
import { useNavigate, Link } from 'react-router-dom';

const TermsOfUse = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <div className="bg-white border-b border-grey-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 text-primary-600 hover:text-primary-700 flex items-center gap-2 font-medium"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-grey-900">Terms of Use</h1>
          <p className="text-grey-600 mt-2">Effective Date: January 1, 2025</p>
          <p className="text-grey-500 text-sm mt-1">Last Updated: November 27, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg p-8 shadow-sm">
          <div className="prose prose-sm max-w-none text-grey-700 space-y-8">

            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">1. Introduction and Acceptance</h2>
              <p>
                Welcome to Pebble. These Terms of Use ("Terms") constitute a legally binding agreement between you and Pebble The App, Inc., 
                a Delaware corporation ("Pebble," "we," "us," or "our") governing your access to and use of the Pebble platform, website, mobile applications, 
                and all related services (collectively, the "Service").
              </p>
              <p className="mt-3">
                <strong>BY ACCESSING OR USING THE SERVICE, YOU AGREE TO BE BOUND BY THESE TERMS.</strong> If you do not agree to these 
                Terms, you may not access or use the Service. These Terms apply to all users of the Service, including Event Organizers, 
                Marketplace Vendors/Sellers, and general visitors.
              </p>
              <p className="mt-3">
                We may modify these Terms at any time. Material changes will be communicated via email or prominent notice on the Service 
                at least 30 days before taking effect. Your continued use of the Service after changes become effective constitutes 
                acceptance of the revised Terms.
              </p>
            </section>

            {/* Definitions */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">2. Definitions</h2>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>"Event Organizer" or "EO"</strong>: A user who creates, manages, and hosts events through the Service.</li>
                <li><strong>"Marketplace Vendor," "Marketplace Seller," or "MPS"</strong>: A user who registers to participate as a vendor or seller at events created by Event Organizers.</li>
                <li><strong>"Registration Fee"</strong>: The fee charged by Event Organizers to Vendors for participation in an event.</li>
                <li><strong>"Platform Fee" or "Service Fee"</strong>: The percentage fee (currently 15%) retained by Pebble from vendor registration payments.</li>
                <li><strong>"User," "you," or "your"</strong>: Any individual or entity accessing or using the Service.</li>
                <li><strong>"Content"</strong>: All text, images, videos, documents, event listings, business profiles, and other materials uploaded to or displayed on the Service.</li>
                <li><strong>"Connected Account"</strong>: A Stripe account connected to Pebble for payment processing purposes.</li>
                <li><strong>"Required Documents"</strong>: Documents uploaded by Event Organizers that Vendors must acknowledge, sign, or review as part of event registration.</li>
                <li><strong>"Vendor Documents"</strong>: Compliance documents uploaded by Vendors, including insurance certificates, permits, licenses, and other required documentation.</li>
                <li><strong>"Electronic Signature"</strong>: A typed name or other electronic acknowledgment used to signify agreement to terms or documents.</li>
              </ul>
            </section>

            {/* Platform Role */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">3. Platform Role and Disclaimers</h2>
              <p>
                <strong>Pebble is a technology platform and marketplace intermediary.</strong> We provide the technology infrastructure 
                that enables Event Organizers to create and manage events, and Marketplace Vendors to discover and register for those events.
              </p>
              <p className="mt-3">
                <strong>We are not a party to transactions between users.</strong> Pebble is not an event organizer, event planner, 
                vendor, or seller. We do not own, control, offer, manage, or deliver any events, products, or services listed on the platform.
              </p>
              <p className="mt-3">
                <strong>Limited Payment Agent Role.</strong> Pebble acts as a limited payment collection agent solely for the purpose 
                of accepting payments from Vendors on behalf of Event Organizers. Our acceptance of payments does not make us a party 
                to the underlying transaction between Event Organizers and Vendors.
              </p>
              <p className="mt-3">
                <strong>No Verification of Users.</strong> While we may verify certain information provided by users, we do not guarantee 
                the accuracy, completeness, or reliability of any user information, event listings, or vendor profiles. Users are 
                responsible for conducting their own due diligence.
              </p>
            </section>

            {/* Account Registration */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">4. Account Registration and Security</h2>
              <p>
                To access certain features of the Service, you must create an account. When registering, you agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security and confidentiality of your login credentials</li>
                <li>Immediately notify us of any unauthorized access to your account</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
              <p className="mt-3">
                You must be at least 18 years old (or the age of majority in your jurisdiction) to create an account. By creating an 
                account, you represent that you meet this requirement and have the legal authority to enter into these Terms.
              </p>
              <p className="mt-3">
                We reserve the right to suspend or terminate your account at any time for any reason, including violation of these Terms, 
                fraudulent activity, or at the request of law enforcement.
              </p>
            </section>

            {/* Event Organizer Terms */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">5. Event Organizer Terms</h2>
              <p>
                If you use the Service as an Event Organizer, the following additional terms apply:
              </p>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">5.1 Event Listings</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>You are solely responsible for the accuracy and completeness of your event listings</li>
                <li>You must have the legal right and authority to host the event at the stated location</li>
                <li>You must comply with all applicable laws, regulations, permits, and venue requirements</li>
                <li>You must not list events that are fraudulent, misleading, or violate any third-party rights</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">5.2 Vendor Management</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>You have sole discretion to approve or deny vendor applications</li>
                <li>You are responsible for communicating event requirements, logistics, and changes to vendors</li>
                <li>You must treat all vendors fairly and in compliance with applicable anti-discrimination laws</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">5.3 Required Documents and Document Management</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>You may upload Required Documents that Vendors must acknowledge, review, or sign as part of the registration process</li>
                <li>You are solely responsible for the content, accuracy, and legal enforceability of any Required Documents you upload</li>
                <li>Required Documents may include venue regulations, liability waivers, rules of conduct, safety guidelines, vendor agreements, and insurance requirements</li>
                <li>You may specify whether documents require acknowledgment only, electronic signature, or are informational (read-only)</li>
                <li>Electronic signatures collected through the Service constitute legally binding agreements under Delaware law (6 Del. C. § 12A-101 et seq.) and the federal ESIGN Act (15 U.S.C. § 7001 et seq.)</li>
                <li>You may view and track vendor acknowledgments and signatures through your Event Organizer dashboard</li>
                <li>You are responsible for retaining copies of signed documents as needed for your records</li>
                <li>You may require Vendors to upload compliance documents such as insurance certificates, permits, and licenses</li>
                <li>You are responsible for reviewing and verifying Vendor-uploaded documents before event participation</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">5.4 Payment Collection via Stripe Connect</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>To receive vendor registration payments, you must connect a Stripe account</li>
                <li>You authorize Pebble to collect payments from vendors on your behalf</li>
                <li>Pebble retains a Platform Fee of 15% from each vendor registration payment</li>
                <li>The remaining 85% is transferred to your connected Stripe account</li>
                <li>Charitable donations (such as tree planting donations) are excluded from Platform Fee calculations</li>
                <li>You are responsible for any chargebacks, disputes, or refunds related to your events</li>
                <li>Stripe's terms of service also apply to your use of payment features</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">5.5 Subscription Plans</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Event Organizer subscription plans are paid from the start of service with no free trial period</li>
                <li>Basic Plan: $249/month - up to 3 events per month, 25 vendors per event</li>
                <li>Pro Plan: $549/month - up to 10 events per month, 50 vendors per event</li>
                <li>Annual billing is available at a 15% discount</li>
                <li>The 15% Platform Fee on vendor registrations applies to all plans</li>
                <li>Subscription fees are billed in advance on a recurring basis according to your selected plan</li>
                <li>You may cancel your subscription at any time; cancellation takes effect at the end of the billing cycle</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">5.6 Event Cancellation</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>If you cancel an event, you are responsible for issuing refunds to registered vendors</li>
                <li>You must notify all registered vendors promptly of any cancellation or material changes</li>
                <li>Pebble is not liable for any losses resulting from event cancellation</li>
              </ul>
            </section>

            {/* Marketplace Vendor Terms */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">6. Marketplace Vendor/Seller Terms</h2>
              <p>
                If you use the Service as a Marketplace Vendor or Seller, the following additional terms apply:
              </p>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.1 Vendor Registration</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>You must provide accurate business and contact information</li>
                <li>Event registration is subject to approval by the Event Organizer</li>
                <li>Approval is at the sole discretion of the Event Organizer</li>
                <li>You must pay all applicable registration fees before your participation is confirmed</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.2 Document Acknowledgment and Signatures</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Event Organizers may require you to acknowledge or sign documents as part of the registration process</li>
                <li>By clicking "Acknowledge" or providing your electronic signature, you agree to be bound by the terms of the applicable document</li>
                <li>Electronic signatures you provide through the Service are legally binding under Delaware law and the federal ESIGN Act</li>
                <li>You are responsible for reviewing all Required Documents before acknowledgment or signature</li>
                <li>Your acknowledgments and signatures are recorded with timestamps and stored for record-keeping purposes</li>
                <li>You may not complete registration for events that require document acknowledgment until all required documents have been acknowledged</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.3 Vendor Document Uploads</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>You may be required to upload compliance documents such as insurance certificates, food permits, business licenses, health permits, and tax documents</li>
                <li>You represent and warrant that all documents you upload are accurate, current, and valid</li>
                <li>You are responsible for maintaining current documentation and updating expired documents</li>
                <li>Event Organizers may review your uploaded documents as part of their approval process</li>
                <li>Pebble does not verify the authenticity or validity of uploaded documents</li>
                <li>Failure to maintain current documentation may result in denial of event registration</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.4 Payment Obligations</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Registration fees are collected via Stripe and processed upon approval</li>
                <li>All payments are final and non-refundable unless otherwise stated by the Event Organizer</li>
                <li>You authorize Pebble to charge your payment method for applicable fees</li>
                <li>Failed payments may result in cancellation of your event registration</li>
                <li>Optional charitable donations (such as tree planting) are processed separately and are non-refundable</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.5 Vendor Responsibilities</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>You must comply with all Event Organizer requirements and instructions</li>
                <li>You are responsible for obtaining all necessary permits, licenses, and insurance</li>
                <li>You must comply with all applicable laws regarding your products or services</li>
                <li>You are responsible for collecting and remitting applicable sales taxes</li>
                <li>You must maintain appropriate product liability and general liability insurance</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.6 Products and Services</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>You represent that all products and services you offer are legal and safe</li>
                <li>You must not sell counterfeit, stolen, or prohibited items</li>
                <li>You are solely responsible for the quality and safety of your offerings</li>
                <li>Pebble has no liability for any products or services sold by vendors</li>
              </ul>
            </section>

            {/* Fees and Payments */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">7. Fees, Payments, and Billing</h2>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">7.1 Platform Fees</h3>
              <p>
                Pebble charges a Platform Fee of <strong>15%</strong> on all vendor registration payments processed through the Service. 
                This fee is automatically deducted before funds are transferred to Event Organizers. Charitable donations collected 
                through the Service (such as tree planting donations) are excluded from Platform Fee calculations.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">7.2 Subscription Fees</h3>
              <p>
                Event Organizers are charged subscription fees based on their selected plan. Subscription fees are billed in advance 
                and are non-refundable except as required by law. You authorize us to charge your payment method automatically for 
                recurring fees.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">7.3 Payment Processing</h3>
              <p>
                All payment processing is handled by Stripe. By using our payment features, you also agree to 
                <a href="https://stripe.com/legal" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer"> Stripe's Terms of Service</a>.
                Pebble is not responsible for any errors, delays, or issues caused by payment processors.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">7.4 Taxes</h3>
              <p>
                You are solely responsible for determining and paying all applicable taxes related to your use of the Service. 
                Pebble may collect and remit certain taxes where required by law. Event Organizers and Vendors are responsible for 
                their own tax compliance and reporting.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">7.5 Chargebacks and Disputes</h3>
              <p>
                Event Organizers are fully liable for all chargebacks, disputes, and refunds related to vendor registrations. 
                If a chargeback occurs, we may deduct the disputed amount plus any associated fees from your account balance or 
                future payouts.
              </p>
            </section>

            {/* Refund Policy */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">8. Refund Policy</h2>
              <p>
                <strong>Subscription Fees:</strong> Subscription fees are generally non-refundable. If you cancel during a billing 
                cycle, you will retain access until the end of that cycle. Refund requests may be considered on a case-by-case basis 
                within 14 days of initial purchase.
              </p>
              <p className="mt-3">
                <strong>Vendor Registration Fees:</strong> Refunds for vendor registration fees are at the sole discretion of the 
                Event Organizer. Pebble does not control and is not responsible for Event Organizer refund policies. The Platform 
                Fee (15%) is non-refundable once a transaction is processed.
              </p>
              <p className="mt-3">
                <strong>Charitable Donations:</strong> Donations made through the Service (such as tree planting donations) are 
                non-refundable.
              </p>
              <p className="mt-3">
                <strong>Event Cancellation:</strong> If an event is cancelled by the Event Organizer, vendors should contact the 
                Event Organizer directly for refund requests. Pebble may assist in facilitating refunds but is not obligated to do so.
              </p>
            </section>

            {/* User Content */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">9. User Content and Intellectual Property</h2>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">9.1 Your Content</h3>
              <p>
                You retain ownership of all Content you submit to the Service, including documents, images, and text. By submitting Content, you grant Pebble a worldwide, 
                non-exclusive, royalty-free, sublicensable license to use, reproduce, modify, adapt, publish, translate, and distribute 
                your Content in connection with operating and promoting the Service.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">9.2 Document Storage</h3>
              <p>
                Documents you upload to the Service (including Required Documents, Vendor Documents, and signed agreements) are stored 
                securely using industry-standard encryption. Pebble provides document storage as a convenience feature and is not 
                responsible for maintaining permanent archives of your documents. You are responsible for retaining your own copies 
                of important documents.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">9.3 Content Restrictions</h3>
              <p>You represent and warrant that your Content:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Does not infringe any third-party intellectual property rights</li>
                <li>Does not violate any laws or regulations</li>
                <li>Does not contain false, misleading, or deceptive information</li>
                <li>Does not contain malware, viruses, or harmful code</li>
                <li>Does not contain obscene, defamatory, or harassing material</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">9.4 Pebble's Intellectual Property</h3>
              <p>
                The Service, including its design, features, software, and all Pebble-created content, is owned by Pebble and protected 
                by intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of the Service without 
                our written permission.
              </p>
            </section>

            {/* Electronic Signatures */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">10. Electronic Signatures and Records</h2>
              <p>
                By using the Service, you consent to conducting business electronically and agree that:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Electronic signatures provided through the Service have the same legal effect as handwritten signatures</li>
                <li>Electronic records of your acknowledgments and signatures satisfy any legal requirements for written records</li>
                <li>You waive any rights or requirements under any laws that require an original signature or delivery of non-electronic records</li>
                <li>The Service's records of your electronic signatures and acknowledgments are admissible as evidence in any legal proceeding</li>
              </ul>
              <p className="mt-3">
                This consent is provided pursuant to the Delaware Uniform Electronic Transactions Act (6 Del. C. § 12A-101 et seq.) 
                and the federal Electronic Signatures in Global and National Commerce Act (15 U.S.C. § 7001 et seq.).
              </p>
            </section>

            {/* Prohibited Conduct */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">11. Prohibited Conduct</h2>
              <p>You agree not to engage in any of the following prohibited activities:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Violating any applicable laws, regulations, or third-party rights</li>
                <li>Creating fraudulent, misleading, or deceptive event listings or vendor profiles</li>
                <li>Uploading falsified documents, forged signatures, or fraudulent compliance documentation</li>
                <li>Collecting payments outside the Pebble platform to circumvent fees</li>
                <li>Impersonating another person or entity or misrepresenting your affiliation</li>
                <li>Harassing, threatening, or abusing other users</li>
                <li>Posting or transmitting spam, unsolicited promotions, or chain letters</li>
                <li>Uploading viruses, malware, or other harmful code</li>
                <li>Attempting to gain unauthorized access to the Service or other users' accounts</li>
                <li>Interfering with or disrupting the Service or servers</li>
                <li>Using automated means to access or scrape the Service without permission</li>
                <li>Reselling or redistributing any part of the Service</li>
                <li>Engaging in money laundering, fraud, or other financial crimes</li>
                <li>Selling illegal, counterfeit, stolen, or prohibited products</li>
                <li>Violating any community guidelines or policies we may publish</li>
              </ul>
            </section>

            {/* Disclaimers */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">12. Disclaimers</h2>
              <p className="uppercase font-semibold">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, 
                STATUTORY, OR OTHERWISE. TO THE FULLEST EXTENT PERMITTED BY LAW, PEBBLE DISCLAIMS ALL WARRANTIES, INCLUDING BUT 
                NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
              </p>
              <p className="mt-3">
                We do not warrant that the Service will be uninterrupted, secure, or error-free, that defects will be corrected, 
                or that the Service or servers are free of viruses or harmful components.
              </p>
              <p className="mt-3">
                Pebble does not endorse or guarantee the quality, safety, legality, or suitability of any events, vendors, products, 
                or services listed on the platform. All transactions between Event Organizers and Vendors are conducted at your own risk.
              </p>
              <p className="mt-3">
                <strong>Document Disclaimer:</strong> Pebble does not review, verify, or guarantee the legal sufficiency or enforceability 
                of any documents uploaded to the Service. Event Organizers are responsible for ensuring their Required Documents comply 
                with applicable laws. Vendors are responsible for the accuracy and validity of their uploaded documents.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">13. Limitation of Liability</h2>
              <p className="uppercase font-semibold">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL PEBBLE, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, 
                OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING 
                BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN 
                CONNECTION WITH YOUR USE OF THE SERVICE.
              </p>
              <p className="mt-3 uppercase font-semibold">
                PEBBLE'S TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE 
                SHALL NOT EXCEED THE GREATER OF: (A) THE AMOUNT YOU PAID TO PEBBLE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, 
                OR (B) ONE HUNDRED DOLLARS ($100).
              </p>
              <p className="mt-3">
                Some jurisdictions do not allow the exclusion or limitation of certain damages, so some of the above limitations 
                may not apply to you.
              </p>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">14. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Pebble, its affiliates, officers, directors, employees, agents, 
                and licensors from and against any and all claims, damages, losses, liabilities, costs, and expenses (including 
                reasonable attorneys' fees) arising out of or relating to:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any applicable laws or regulations</li>
                <li>Your Content, documents, or any transactions you conduct through the Service</li>
                <li>Any documents you require vendors to sign or acknowledge</li>
                <li>Any dispute between you and another user</li>
                <li>Your failure to pay applicable taxes</li>
                <li>Any products or services you sell as a vendor</li>
                <li>Any events you organize as an Event Organizer</li>
              </ul>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">15. Dispute Resolution and Arbitration</h2>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">15.1 Informal Resolution</h3>
              <p>
                Before initiating any formal dispute resolution, you agree to contact us at legal@pebbletheapp.com and attempt to resolve 
                any dispute informally for at least 30 days.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">15.2 Binding Arbitration</h3>
              <p>
                If we cannot resolve a dispute informally, you and Pebble agree to resolve any dispute through binding individual 
                arbitration administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules. 
                The arbitration will be conducted in English and held in the county where you reside or another mutually agreed location.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">15.3 Class Action Waiver</h3>
              <p className="font-semibold">
                YOU AND PEBBLE AGREE THAT EACH PARTY MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, 
                AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION. Unless both 
                parties agree, the arbitrator may not consolidate more than one person's claims.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">15.4 Exceptions</h3>
              <p>
                This arbitration agreement does not apply to: (a) claims that may be brought in small claims court; (b) claims 
                seeking injunctive relief related to intellectual property; or (c) claims where arbitration is prohibited by law.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">15.5 Opt-Out</h3>
              <p>
                You may opt out of this arbitration agreement by sending written notice to legal@pebbletheapp.com within 30 days of first 
                accepting these Terms. The notice must include your name, address, and a clear statement that you wish to opt out.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">16. Termination</h2>
              <p>
                <strong>Termination by You:</strong> You may terminate your account at any time by contacting us or using account 
                settings. Upon termination, your right to use the Service immediately ceases.
              </p>
              <p className="mt-3">
                <strong>Termination by Pebble:</strong> We may suspend or terminate your access to the Service at any time, with or 
                without cause and with or without notice. Reasons for termination include but are not limited to: violation of these 
                Terms, fraudulent activity, prolonged inactivity, or requests by law enforcement.
              </p>
              <p className="mt-3">
                <strong>Effect of Termination:</strong> Upon termination, all outstanding payment obligations remain due. We may 
                retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our 
                agreements. Content you posted may remain on the Service after termination. Documents and signatures may be retained 
                for legal compliance purposes.
              </p>
            </section>

            {/* General Provisions */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">17. General Provisions</h2>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">17.1 Governing Law</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, 
                without regard to conflict of law principles. Any legal action or proceeding arising out of or relating to these 
                Terms shall be brought exclusively in the state or federal courts located in Delaware.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">17.2 Entire Agreement</h3>
              <p>
                These Terms, together with our Privacy Policy and any additional terms you agree to, constitute the entire agreement 
                between you and Pebble regarding the Service.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">17.3 Severability</h3>
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force 
                and effect.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">17.4 Waiver</h3>
              <p>
                Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">17.5 Assignment</h3>
              <p>
                You may not assign or transfer these Terms or your rights hereunder without our prior written consent. Pebble may 
                freely assign these Terms.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">17.6 Force Majeure</h3>
              <p>
                Pebble shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, 
                including but not limited to acts of God, natural disasters, war, terrorism, labor disputes, or government actions.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">18. Contact Information</h2>
              <p>If you have any questions about these Terms of Use, please contact us:</p>
              <div className="mt-3 p-4 bg-grey-50 rounded-lg">
                <p><strong>Pebble The App, Inc.</strong></p>
                <p className="mt-1">A Delaware Corporation</p>
                <p className="mt-2">Email: legal@pebbletheapp.com</p>
                <p>Support: support@pebbletheapp.com</p>
                <p className="mt-2">For dispute resolution: legal@pebbletheapp.com</p>
              </div>
            </section>

            {/* Related Documents */}
            <section className="border-t border-grey-200 pt-6">
              <h2 className="text-xl font-semibold text-grey-900 mb-4">Related Documents</h2>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy-policy" className="text-primary-600 hover:underline">Privacy Policy</Link>
                </li>
              </ul>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
